<template>
    <section>
        <div class="review-container">
            <DsBox padding-bottom="spacing-200">
                <OrderReviewRow
                    :label="$t('MoneyLib.subtotal')"
                    :value="$n(invoice.subTotalAmount || 0, 'currency')"
                />
            </DsBox>

            <DsBox v-if="hasTax" padding-bottom="spacing-200">
                <DsStack space="spacing-100">
                    <OrderReviewRow
                        v-for="taxSummaryItem in invoice.taxSummaryItems"
                        :key="taxSummaryItem.name"
                        :label="$t('InvoiceTotals.taxName', { name: taxSummaryItem.name, rate: taxSummaryItem.rate })"
                        :value="$n(taxSummaryItem.amount || 0, 'currency')"
                        data-qa="tax-row"
                    />
                </DsStack>
            </DsBox>

            <DsStack space="spacing-100">
                <InvoiceDiscount
                    :is-recurring="isRecurring"
                    :invoice="invoice"
                />

                <OrderReviewRow
                    v-if="!isRefunded"
                    :label="$t('MoneyLib.paid')"
                    :value="$n(-invoice.totalPaid || 0, 'currency')"
                />

                <OrderReviewRow
                    v-else
                    :label="$t('InvoiceTotals.totalRefunded')"
                    :value="$n(invoice.refundTotal || 0, 'currency')"
                />

                <InvoiceDeposit
                    v-if="!isRefunded"
                    :is-recurring="isRecurring"
                    :total-due="totalDue"
                    :invoice="invoice"
                />
            </DsStack>

            <DsBox v-if="displayGstText" padding-top="spacing-300" padding-bottom="spacing-100">
                <div class="gst-price-included">
                    {{ $t('InvoiceTotals.gst.tax') }}
                </div>
            </DsBox>
        </div>
    </section>
</template>

<script>
import InvoiceDeposit from './InvoiceDeposit.vue';
import InvoiceDiscount from './InvoiceDiscount.vue';
import { invoiceTotalDue, invoiceIsRefunded } from '../../money.util';
import OrderReviewRow from '../order/OrderReviewRow.vue';
import { DsBox, DsStack } from '@infusionsoft/design-system';

export default {
    components: {
        InvoiceDeposit,
        InvoiceDiscount,
        OrderReviewRow,
        DsBox,
        DsStack,
    },

    props: {
        invoice: {
            type: Object,
            default: () => {
                return {
                    totalDue: 0,
                    totalPaid: 0,
                    total: 0,
                    status: '',
                    refundTotal: 0,
                    orderItems: [],
                    taxSummaryItems: [],
                };
            },
        },
        appSalesInfo: {
            type: Object,
            default: () => ({}),
        },
    },

    computed: {
        subtotal() {
            return this.invoice?.subTotalAmount;
        },

        displayGstText() {
            const { taxIdType } = this.appSalesInfo;

            return Boolean(taxIdType === 'AUD');
        },

        isRefunded() {
            return invoiceIsRefunded(this.invoice);
        },

        totalDue() {
            return invoiceTotalDue(this.invoice);
        },

        orderItems() {
            return this.invoice.orderItems;
        },

        isRecurring() {
            if (this.orderItems) {
                return this.orderItems?.some(({ recurringBilling }) => recurringBilling === true);
            }

            return false;
        },

        taxLineItem() {
            return this.orderItems?.filter(({ type }) => type === 'Tax');
        },

        hasTax() {
            return this.hasInternalTax || this.hasPublicTax;
        },

        hasInternalTax() {
            return this.taxLineItem?.length > 0;
        },

        hasPublicTax() {
            return this.invoice.totalTaxAmount > 0;
        },
    },
};
</script>

<style lang="scss" scoped>
@import '../../styles/common';

.review-container {
    font-size: $font-size-md;
    padding: $spacing-300 $spacing-500;
}

@media only screen and ($small) {
    .review-container {
        font-size: $font-size-sm;
        padding: $spacing-200;
    }
}

.gst-price-included {
    color: $color-ink-800;
    font-size: $font-size-md;
    text-align: end;
}
</style>
